import { useRef, useEffect } from 'react';
import { useMobileChart } from '../context/MobileChartContext';

/**
 * A hook that enhances chart components with responsive behavior
 * 
 * On mobile:
 * - Charts open in a fullscreen overlay for better viewing
 * - Touch interactions are optimized
 * 
 * On desktop:
 * - Charts can be opened in a modal/dialog
 * - Mouse interactions are preserved
 * 
 * @param title The title of the chart (displayed in overlays and modals)
 * @param chartHeight Optional height override for the chart (defaults to 80vh for mobile overlays)
 * @returns A ref object with enhanced functionality
 */
export const useResponsiveCharts = (title: string, chartHeight: string = '80vh') => {
  const { isMobile, openChart } = useMobileChart();
  const chartRef = useRef<any>({ node: null });
  
  useEffect(() => {
    // Store original functions if they exist
    const originalOpenModal = chartRef.current.openModal;
    const originalGetChartComponent = chartRef.current.getChartComponent;
    
    // Enhanced openModal function that works on both mobile and desktop
    chartRef.current.openModal = () => {
      if (isMobile) {
        // On mobile, use the MobileChartOverlay via openChart
        const chartComponent = chartRef.current.getChartComponent?.();
        
        if (chartComponent) {
          openChart('custom', title, (
            <div style={{ height: chartHeight }}>
              {chartComponent}
            </div>
          ));
        }
      } else if (originalOpenModal) {
        // On desktop, use the original modal functionality
        originalOpenModal();
      }
    };
    
    // Cleanup function to restore original methods
    return () => {
      if (chartRef.current) {
        chartRef.current.openModal = originalOpenModal;
        chartRef.current.getChartComponent = originalGetChartComponent;
      }
    };
  }, [isMobile, openChart, title, chartHeight]);
  
  return chartRef;
}; 